import { z } from "zod";

import type { ArrayBinaryOperator, TernaryOperator } from "../../operators.ts";
import type { IValue, PrimitiveArrayValue, PrimitiveSingleValue, ResolvableArrayValue } from "../../values.ts";
import type { IConditionFor } from "../common.ts";

export interface IArrayBinaryCondition<
  TMultiValue extends ResolvableArrayValue | PrimitiveArrayValue,
  TValue extends IValue | PrimitiveSingleValue,
  TOperator extends ArrayBinaryOperator = ArrayBinaryOperator,
> extends IConditionFor<TOperator> {
  subject: TMultiValue;
  object: TValue;
}

export interface IArrayTernaryCondition<
  TMultiValue extends ResolvableArrayValue | PrimitiveArrayValue,
  TValue extends IValue | PrimitiveSingleValue,
  TOperator extends TernaryOperator = TernaryOperator,
> extends IConditionFor<TOperator> {
  subject: TMultiValue;
  left: TValue;
  right: TValue;
}

// Zod schema factory functions for array conditions
export function arrayBinaryCondition<
  TType extends z.ZodTypeAny,
  TOperator extends z.ZodTypeAny,
  TArrayValue extends z.ZodTypeAny,
  TValue extends z.ZodTypeAny,
>(typeSchema: TType, operatorSchema: TOperator, arrayValueSchema: TArrayValue, valueSchema: TValue) {
  return z.object({
    type: typeSchema,
    operator: operatorSchema,
    subject: arrayValueSchema,
    object: valueSchema,
  });
}

export function arrayTernaryCondition<
  TType extends z.ZodTypeAny,
  TOperator extends z.ZodTypeAny,
  TArrayValue extends z.ZodTypeAny,
  TValue extends z.ZodTypeAny,
>(typeSchema: TType, operatorSchema: TOperator, arrayValueSchema: TArrayValue, valueSchema: TValue) {
  return z.object({
    type: typeSchema,
    operator: operatorSchema,
    subject: arrayValueSchema,
    left: valueSchema,
    right: valueSchema,
  });
}
